import { useAtomValue } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';

import { Gutter, GutterBleed, Stack } from '@crossnokaye/ux-common';

import { SummaryConfig } from '@repo/sdk/models/components';

import { SidebarTemplate } from '@/components/templates';
import { EquipmentDetailsSidebar } from '@/features/equipment-browser/equipment-details/sidebar/equipment-details-sidebar';
import { Tab } from '@/ux-common/components/tab/tab';
import { TabGroup } from '@/ux-common/components/tab-group/tab-group';

import { EquipmentDetailsAlarmTableSection } from './alarm-table/equipment-details-alarm-table-section';
import { EquipmentDetailsForm } from './details-form/equipment-details-form';
import { EquipmentDetailsFooter } from './footer/equipment-details-footer';
import { EquipmentDetailsMetrics } from './metrics/equipment-details-metrics';
import { EquipmentDetailsPageHeader } from './page-header/equipment-details-page-header';
import { equipmentLoadingAtom } from './state/equipment-response.state';

interface EquipmentDetailsViewProps {
  equipment: SummaryConfig | undefined;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

export const EquipmentDetailsView = ({
  equipment, // todo: switch to only passing in the equipmentId.  the equipment data will be fetched via api
  onClose,
}: EquipmentDetailsViewProps): React.ReactNode => {
  const isLoading = useAtomValue(equipmentLoadingAtom);

  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedBadge, setSelectedBadge] = useState<string>('');
  const [availableBadges, setAvailableBadges] = useState<string[]>([]);

  // Check if element or any parent is hidden
  const isElementOrParentHidden = (element: HTMLElement): boolean => {
    let current: HTMLElement | null = element;
    while (current && current !== containerRef.current) {
      const style = window.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden' || current.hidden) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  };

  // Update available badges when DOM is ready
  useEffect(() => {
    const allSections =
      containerRef.current?.querySelectorAll('[data-component="Accordion"][id^="sections-"]') ?? [];
    const visibleSections = Array.from(allSections).filter((section) => {
      const element = section as HTMLElement;
      return !isElementOrParentHidden(element);
    });
    const badges = visibleSections.map((section) => section.id.replace('sections-', ''));
    setAvailableBadges(badges);
  }, [isLoading]);

  // Find accordion section by title using id selector
  const findAccordionSectionByTitle = (title: string): HTMLElement | null => {
    return (
      (containerRef.current?.querySelector(
        `[data-component="Accordion"][id="sections-${title}"]`
      ) as HTMLElement) ?? null
    );
  };

  // Scroll to section when badge selection changes
  useEffect(() => {
    if (!selectedBadge) return;

    const sectionElement = findAccordionSectionByTitle(selectedBadge);
    if (!sectionElement) return;

    // Use setTimeout to ensure DOM is ready after state update
    const timeoutId = setTimeout(() => {
      sectionElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [selectedBadge]);

  return (
    <SidebarTemplate
      ref={containerRef}
      className="h-full"
      leftColumn={
        <div className="hidden @2xl/sidebar:block h-full pl-3 lg:pl-5 w-[18.75rem]">
          <EquipmentDetailsSidebar equipment={equipment} isLoading={isLoading} />
        </div>
      }
    >
      {/* Flex container to push footer to bottom when content is short */}
      <div className="flex flex-col">
        {/* Header */}
        <Gutter
          orientation="horizontal"
          size="responsive"
          className="sticky top-0 z-sticky bg-background pb-stack-sm"
        >
          <EquipmentDetailsPageHeader
            equipment={equipment}
            isLoading={isLoading}
            onClose={onClose}
          />

          <TabGroup
            items={availableBadges.map((badge) => ({ id: badge, data: { label: badge } }))}
            className="@2xl:hidden py-stack-sm"
            onSelectionChange={(items) => setSelectedBadge((items[0]?.id as string) ?? '')}
            scrollable
            selectedIds={[selectedBadge]}
          >
            {({ tabs }) =>
              tabs.map(({ tab, state, disabled, onToggle }) => {
                return (
                  <Tab key={tab.id} data-state={state} disabled={disabled} onClick={onToggle}>
                    {tab.data?.label}
                  </Tab>
                );
              })
            }
          </TabGroup>
        </Gutter>

        <Gutter className="block @2xl/sidebar:hidden" size="responsive">
          <EquipmentDetailsSidebar equipment={equipment} isLoading={isLoading} />
        </Gutter>

        {/* Main content area */}
        <GutterBleed size="responsive">
          <Stack
            gap="thin"
            className="equipment-details-view-stack"
            data-testid="equipment-details-content"
          >
            <EquipmentDetailsMetrics />
            {/* Details form contains Settings, sequencer, and defrost schedule */}
            <EquipmentDetailsForm />
            {/*
            In part of of this PR, I will make a <SequencerEditor /> component that will select between the Compressor or Condenser Sequencers.
            For now, we will just show the Condenser Sequencer Editor.
          */}
            <EquipmentDetailsAlarmTableSection
              deviceId={equipment?.referenceId ?? ''}
              containerRef={containerRef}
            />
          </Stack>
        </GutterBleed>

        {/* Spacer to push footer to bottom when content is short */}
        <div className="flex-1" />

        {/* Footer - stays at bottom */}
        <EquipmentDetailsFooter />
      </div>
    </SidebarTemplate>
  );
};
